import { useContext, useEffect, useState } from "react";
import Div100vh from "react-div-100vh";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { SoecoButton } from "../../components/SoecoButton";
import { useSnackBar } from "../../context/SnackbarContext";
import { useGetDesigners } from "../../hooks/designerHooks";
import { useGetManufacturers } from "../../hooks/manufacturerHooks";
import { useAddProduct, useGetProduct } from "../../hooks/productHooks";
import { useGetProductStatuses } from "../../hooks/productStatusHooks";
import { useGetActions } from "../../hooks/actionHooks";
import { queryClient } from "../../hooks/queryClient";
import { useAppLocation } from "../../hooks/useAppLocation";
import { imagesUrl } from "../../services/endpoints";
import { ErrorRecord, OptionsArray, OptionsObject, Product, ProductCategory } from "../../types";
import {
    createReadOnlyOptions,
    createReadOnlySortedOptions,
    createReadOnlySortedOptionsMaterial,
    createReadOnlySortedOptionsProductStatuses,
} from "../../utils/util";
import { CameraForm } from "./CameraForm";
import { DetailsForm } from "./DetailsForm";
import { FactoryForm } from "./FactoryForm";
import { PriceForm } from "./PriceForm";
import { ProductCategoryForm } from "./ProductCategoryForm";
import { ProductOverview } from "./ProductOverview";
import { SoecoStepper } from "./SoecoStepper";
import { validateNewProduct } from "./validateNewProduct";
import { PredictProduct, ProductOptions } from "./PredictProduct";
import { useGetProductCategories } from "../../hooks/productCategoryHooks";
import { useGetProductMainCategories } from "../../hooks/productMainCategoryHooks";
import { Typography } from "@mui/material";
import { useGetProductColors } from "../../hooks/productColorHooks";
import YLLWLOGO from "../../assets/Yllw_Inventory.svg";
import { useGetMaterials } from "../../hooks/materialHooks";
import { EmployeeContext } from "../../context/EmployeeContext";
import { useGetEmployee } from "../../hooks/employeeHooks";

export const NewProduct = () => {
    const { companyId, inventoryId } = useParams();
    const mutation = useAddProduct();
    const navigate = useNavigate();
    const { state } = useAppLocation();
    const snackBar = useSnackBar();

    const productQuery = useGetProduct(companyId ?? "", inventoryId ?? "", state?.productId ?? "");
    const { data: manufacturers } = useGetManufacturers();
    const { data: designers } = useGetDesigners();
    const { data: statuses } = useGetProductStatuses();
    const { data: productCategories } = useGetProductCategories();
    const { data: productMainCategories } = useGetProductMainCategories();
    const { data: actions } = useGetActions();
    const { data: colors } = useGetProductColors();
    const { data: materials } = useGetMaterials();
    const { employee } = useContext(EmployeeContext);
    const employeeQuery = useGetEmployee(employee.companyId, employee.id);

    const [step, setStep] = useState(0);
    const [error, setError] = useState<boolean>(false);
    const [errors, setErrors] = useState<ErrorRecord<Product>>({});
    const [defaultPicture, setDefaultPicture] = useState<boolean>(false);

    const [completed, setCompleted] = useState<{
        [k: number]: boolean;
    }>({});

    const [images, setImages] = useState<File[]>([]);
    const [imageUrls, setImageUrls] = useState<string[]>([]);

    const [manufacturer, setManufacturer] = useState<OptionsObject | null>(null);
    const [designer, setDesigner] = useState<OptionsObject | null>(null);
    const [status, setStatus] = useState<OptionsObject | null>(null);
    const [action, setAction] = useState<OptionsObject | null>(null);
    const [material, setMaterial] = useState<OptionsObject | null>(null);

    const [manufacturersOptions, setManufacturersOptions] = useState<OptionsArray>([]);
    const [designersOptions, setDesignersOptions] = useState<OptionsArray>([]);
    const [statusesOptions, setStatusesOptions] = useState<OptionsArray>([]);
    const [actionsOptions, setActionsOptions] = useState<OptionsArray>([]);
    const [materialsOptions, setMaterialsOptions] = useState<OptionsArray>([]);

    const [product, setProduct] = useState<Product>({
        companyId: companyId ?? "",
        inventoryId: inventoryId ?? "",
        name: "",
        id: uuidv4(),
        categories: [],
        imageUrls: [],
        netPrice: "",
        purchasePrice: "",
        marketPrice: "",
        color: "",
        measurements: "",
        comment: "",
        manufacturer: "",
        designer: "",
        co2e: null,
        status: "",
        warehousePlacement: "",
        town: "",
        roomPlacement: "",
        quantity: 1,
        predictions: [],
        predictionUsed: "",
        action: "",
        colors: [],
        material: "",
        height: "",
        width: "",
        depth: "",
        length: "",
        seatHeight: "",
        avalible: "",
        address: "",
    });
    const moveImage = (clickedUrl: string) => {
        setImageUrls(prevUrls => {
            const newUrls = [clickedUrl, ...prevUrls.filter(url => url !== clickedUrl)];

            setImages(prevImages => {
                const index = prevUrls.indexOf(clickedUrl);
                if (index === -1) return prevImages;

                const clickedFile = prevImages[index];
                const newImages = [clickedFile, ...prevImages.filter((_, i) => i !== index)];
                return newImages;
            });

            return newUrls;
        });
    };

    useEffect(() => {
        if (!productQuery.isError && productQuery.data) {
            setProduct({
                companyId: productQuery.data.companyId,
                inventoryId: productQuery.data.inventoryId,
                name: productQuery.data.name ?? "",
                id: uuidv4(),
                categories: productQuery.data.categories,
                imageUrls: [],
                netPrice: productQuery.data.netPrice ?? "",
                purchasePrice: productQuery.data.purchasePrice ?? "",
                marketPrice: productQuery.data.marketPrice ?? "",
                color: productQuery.data.color ?? "",
                measurements: productQuery.data.measurements ?? "",
                co2e: productQuery.data.co2e ?? null,
                comment: productQuery.data.comment ?? "",
                manufacturer: productQuery.data.manufacturer ?? "",
                designer: productQuery.data.designer ?? "",
                status: productQuery.data.status ?? "",
                warehousePlacement: "",
                town: "",
                roomPlacement: "",
                quantity: 1,
                predictions: [],
                predictionUsed: "",
                action: productQuery.data.action ?? "",
                colors: productQuery.data.colors ?? [],
                material: productQuery.data.material ?? "",
                height: productQuery.data.height ?? "",
                width: productQuery.data.width ?? "",
                depth: productQuery.data.depth ?? "",
                length: productQuery.data.length ?? "",
                seatHeight: productQuery.data.seatHeight ?? "",
                avalible: productQuery.data.avalible ?? "",
                address: productQuery.data.address ?? "",
            });
            const loadImages = async () => {
                try {
                    const files: File[] = [];
                    const urls: string[] = [];

                    const imageUrls = productQuery.data?.imageUrls;
                    if (!imageUrls || imageUrls.length === 0) {
                        return;
                    }

                    const blobs = await Promise.all(
                        imageUrls.map(async url => {
                            const res = await fetch(imagesUrl + url);
                            const blob = await res.blob();
                            return { blob, url };
                        }),
                    );

                    for (const { blob, url } of blobs) {
                        const file = new File([blob], url, { type: blob.type });
                        files.push(file);
                        urls.push(URL.createObjectURL(file));
                    }
                    setImages(files);
                    setImageUrls(urls);
                    setStep(6);
                } catch (error) {
                    if (error instanceof Error) {
                        snackBar.showSnackBar(error.message, "error");
                    }
                }
            };
            void loadImages();
        }
    }, [productQuery.data]);

    // Create readonly options for manufacturers, designers and statuses
    useEffect(() => {
        if (manufacturers) {
            const options = createReadOnlySortedOptions(manufacturers);
            setManufacturersOptions(options);
        }
        if (designers) {
            const options = createReadOnlySortedOptions(designers);
            setDesignersOptions(options);
        }
        if (statuses) {
            const options = createReadOnlySortedOptionsProductStatuses(statuses);
            setStatusesOptions(options);
        }
        if (actions) {
            const options = createReadOnlySortedOptionsProductStatuses(actions);
            setActionsOptions(options);
        }
        if (materials) {
            const options = createReadOnlySortedOptionsMaterial(materials);
            setMaterialsOptions(options);
        }
    }, [manufacturers, designers, statuses, actions, materials]);

    // Map existing manufacturer, designer and status to form
    useEffect(() => {
        if (!productQuery.isError && productQuery.data) {
            if (productQuery.data.manufacturer) {
                const manufacturer = manufacturersOptions.find(m => m.id === product.manufacturer);
                handleSelectChange(manufacturer ?? null, "manufacturer");
            }
            if (productQuery.data.manufacturer) {
                const designer = designersOptions.find(d => d.id === product.designer);
                handleSelectChange(designer ?? null, "designer");
            }
            if (productQuery.data.manufacturer) {
                const status = statusesOptions.find(s => s.id === product.status);
                handleSelectChange(status ?? null, "status");
            }
            if (productQuery.data.manufacturer) {
                const action = actionsOptions.find(a => a.id === product.action);
                handleSelectChange(action ?? null, "action");
            }
            if (productQuery.data.material) {
                const material = materialsOptions.find(m => m.id === product.material);
                handleSelectChange(material ?? null, "material");
            }
        }
    }, [
        productQuery.data,
        manufacturersOptions,
        designersOptions,
        statusesOptions,
        actionsOptions,
        materialsOptions,
    ]);

    useEffect(() => {
        setError(false);
        setErrors({});
        setCompleted({ ...completed, [step]: false });
    }, [images, imageUrls, product, step]);

    const getManufacturerLabel = (idOrLabel: string) => {
        const manufacturer = manufacturersOptions.find(
            m => m.label === idOrLabel || m.id === idOrLabel,
        );
        return manufacturer?.label || "";
    };

    const getDesignerLabel = (idOrLabel: string) => {
        const designer = designersOptions.find(d => d.label === idOrLabel || d.id === idOrLabel);
        return designer?.label || "";
    };

    const getMaterialLabel = (idOrLabel: string) => {
        // reomve
        const material = materialsOptions.find(m => m.label === idOrLabel || m.id === idOrLabel);
        return material?.label || "";
    };

    const setOptions = (newProduct: ProductOptions) => {
        const newOptions = {
            ...newProduct,
        };

        if (newProduct.manufacturer) {
            const manufacturer = manufacturersOptions.find(
                m => m.label === newProduct.manufacturer || m.id === newProduct.manufacturer,
            );
            updateOptions(manufacturer ?? null, "manufacturer");
            newOptions.manufacturer = manufacturer?.id;
        }
        if (newProduct.designer) {
            const designer = designersOptions.find(
                d => d.label === newProduct.designer || d.id === newProduct.designer,
            );
            updateOptions(designer ?? null, "designer");
            newOptions.designer = designer?.id;
        }

        //Replace category names with id's
        const newCategories: string[] = [];
        if (newProduct.categories && newProduct.categories.length > 0) {
            for (const category of newProduct.categories) {
                const pc = productCategories?.find(c => c.id === category || c.name === category);
                if (pc) {
                    newCategories.push(pc.id);
                }
            }
            newOptions.categories = newCategories;
        }
        return newOptions;
    };

    const updateOptions = (value: OptionsObject | null, targetField: string) => {
        value && targetField === "manufacturer" && setManufacturer(value);
        value && targetField === "designer" && setDesigner(value);
        value && targetField === "material" && setMaterial(value);
    };

    const validateStep = (e: React.FormEvent<Element>) => {
        e.preventDefault();
        validateNewProduct(
            product,
            images,
            setErrors,
            setError,
            step,
            setStep,
            completed,
            setCompleted,
        );
    };

    const handleSubmit = (e: React.FormEvent<Element>) => {
        e.preventDefault();
        if (!companyId || !inventoryId) {
            // This cannot happen unless routes has been invalidated
            return;
        }
        // This is a bit of a hack to make sure the manufacturer is set to the correct value
        if (product.manufacturer === "1111" && manufacturer) {
            const manufacturerUUID = manufacturersOptions.find(m => m.label === manufacturer.label);
            if (manufacturerUUID) product.manufacturer = manufacturerUUID.id;
        }
        mutation.mutate(
            { companyId, inventoryId, product, images },
            {
                onError: error => {
                    if (error instanceof Error) {
                        snackBar.showSnackBar(error.message, "error");
                    }
                    return;
                },
                onSuccess: () => {
                    snackBar.showSnackBar("Product created", "success");
                    navigate(`/companies/${companyId}/inventories/${inventoryId}`);
                    return;
                },
            },
        );

        if (!navigator.onLine) {
            // Optimistically update to the new value
            const newProductList: Product[] = [
                ...(queryClient.getQueryData(["products", inventoryId]) as Product[]),
                product,
            ] as Product[];
            queryClient.setQueryData(["products", inventoryId], newProductList);

            snackBar.showSnackBar("No internet: Product will be added when online", "success");
            navigate(`/companies/${companyId}/inventories/${inventoryId}`);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setProduct({ ...product, [e.target.name]: e.target.value });
    };

    const handleSelectChange = (
        value: OptionsObject | null,
        targetField: string,
        reason?: string,
    ) => {
        if (value) {
            // Om man väljer ett nytt värde
            setProduct({ ...product, [targetField]: value.id });

            if (targetField === "manufacturer") setManufacturer(value);
            if (targetField === "designer") setDesigner(value);
            if (targetField === "status") setStatus(value);
            if (targetField === "action") setAction(value);
            if (targetField === "material") setMaterial(value);
        } else if (reason === "clear") {
            setProduct({ ...product, [targetField]: null });

            if (targetField === "manufacturer") setManufacturer(null);
            if (targetField === "designer") setDesigner(null);
            if (targetField === "status") setStatus(null);
            if (targetField === "action") setAction(null);
            if (targetField === "material") setMaterial(null);
        }
    };

    const handleProductCategoryChange = (data: ProductCategory) => {
        // Remove if already in list
        if (product.categories?.includes(data.id)) {
            setProduct({
                ...product,
                categories: product.categories.filter(category => category !== data.id),
            });
            // Add if not in list
        } else {
            setProduct({
                ...product,
                categories: [...(product.categories ?? []), data.id],
            });
        }
    };
    const handleProductColorChange = (ids: string[]) => {
        setProduct({
            ...product,
            colors: ids,
        });
    };

    return (
        <>
            <NewProductPageWrapper>
                <HeaderWrapper>
                    <Logo src={YLLWLOGO} />
                    <Typography
                        sx={{
                            fontFamily: "'Yllw Sans a1 241219', sans-serif",
                        }}
                        fontSize={14}
                        fontWeight={"500"}
                        color="#8c8c8c"
                        noWrap
                    >
                        {"New Product"}
                    </Typography>
                </HeaderWrapper>
                <NewProductWrapper>
                    {step !== 6 ? (
                        <SoecoStepper
                            step={step}
                            completed={completed}
                            setCompleted={setCompleted}
                            error={error}
                        />
                    ) : null}

                    <FormWrapper>
                        {step == 0 ? (
                            <CameraForm
                                images={images}
                                setImages={setImages}
                                imageUrls={imageUrls}
                                setImageUrls={setImageUrls}
                                error={error}
                                errors={errors}
                                defaultPicture={defaultPicture}
                                setDefaultPicture={setDefaultPicture}
                            />
                        ) : step == 1 ? (
                            <PredictProduct
                                images={images}
                                companyId={companyId ?? ""}
                                inventoryId={inventoryId ?? ""}
                                product={product}
                                setProduct={setProduct}
                                setOptions={setOptions}
                                getManufacturerLabel={getManufacturerLabel}
                                getDesignerLabel={getDesignerLabel}
                                defaultPicture={defaultPicture}
                                colors={colors || []}
                            />
                        ) : step == 2 ? (
                            <ProductCategoryForm
                                product={product}
                                setProduct={setProduct}
                                handleProductCategoryChange={handleProductCategoryChange}
                                productCategories={productCategories || []}
                                productMainCategories={productMainCategories || []}
                                employee={employeeQuery.data}
                            />
                        ) : step == 3 ? (
                            <DetailsForm
                                product={product}
                                manufacturer={manufacturer}
                                designer={designer}
                                status={status}
                                manufacturersOptions={manufacturersOptions}
                                designersOptions={designersOptions}
                                statusesOptions={statusesOptions}
                                action={action}
                                actionsOptions={actionsOptions}
                                handleChange={handleChange}
                                handleSelectChange={handleSelectChange}
                                handleProductColorChange={handleProductColorChange}
                                colors={colors || []}
                                material={material}
                                materialsOptions={materialsOptions}
                                error={error}
                                errors={errors}
                            />
                        ) : step == 4 ? (
                            <PriceForm
                                product={product}
                                handleChange={handleChange}
                                error={error}
                                errors={errors}
                            />
                        ) : step == 5 ? (
                            <FactoryForm
                                product={product}
                                handleChange={handleChange}
                                error={error}
                                errors={errors}
                            />
                        ) : step == 6 ? (
                            <ProductOverview
                                product={product}
                                manufacturer={manufacturer}
                                designer={designer}
                                status={status}
                                imageUrls={imageUrls}
                                step={step}
                                setStep={setStep}
                                action={action}
                                material={material}
                                moveImage={moveImage}
                            />
                        ) : null}
                    </FormWrapper>

                    <ActionButtons>
                        {step == 0 ? (
                            <SoecoButton
                                fullWidth
                                size="large"
                                color="warning"
                                onClick={() => {
                                    navigate(-1);
                                }}
                            >
                                Cancel
                            </SoecoButton>
                        ) : (
                            <SoecoButton
                                fullWidth
                                size="large"
                                color="warning"
                                onClick={() => {
                                    setStep(step - 1);
                                }}
                            >
                                Back
                            </SoecoButton>
                        )}
                        {/* <CircularProgressWithLabel value={step * 25} /> */}
                        {step == 6 ? (
                            <SoecoButton
                                fullWidth
                                size="large"
                                type="submit"
                                color="primary"
                                onClick={(e: React.FormEvent<Element>) => handleSubmit(e)}
                                disabled={error || mutation.isPending}
                            >
                                Save
                            </SoecoButton>
                        ) : (
                            <SoecoButton
                                form="partial-submit-form"
                                type="submit"
                                fullWidth
                                size="large"
                                color="secondary"
                                disabled={error || mutation.isPending}
                                onClick={validateStep}
                                onSubmit={validateStep}
                            >
                                Next
                            </SoecoButton>
                        )}
                    </ActionButtons>
                </NewProductWrapper>
            </NewProductPageWrapper>
        </>
    );
};

const HeaderWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    //  margin-top: 3px;
`;
const Logo = styled.img`
    width: 120px;
    height: 22px;
    top: 30px;
    left: 135px;
    //margin-top: 20px;
`;
const NewProductPageWrapper = styled(Div100vh)`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    margin-top: 35px;
`;

const NewProductWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 95%;
    height: 100%;
    justify-content: space-between;
    gap: 2rem;
`;

const FormWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 80%;
    overflow: scroll;
`;

export const ActionButtons = styled.div`
    display: flex;
    gap: 24px;
    justify-content: center;
    align-items: center;
    width: 85%;
    margin: 0 auto;
    position: relative;
    margin-bottom: 130px;
`;
